/**
 * THE TURN'S STORY — one authoritative status per turn, projected from what the store already holds.
 *
 * Several renderers used to describe the same operation independently (the closing line, the task
 * stepper, the ask card, the build view), so a screen could say "I drafted a plan" under "Applied"
 * and "Building · done" over an item still pending. This module computes the one status every
 * renderer reads: the phase the task is in, the words for it, the newest activity, whether the
 * reader is being waited on, and how it ended. Pure; nothing here is a state machine — the task's
 * state is R3's, the ask is R2's, the activity is the journal's.
 *
 * The five user-facing phases are a PROJECTION of the fifteen task states, not a replacement: the
 * full transition history stays in the workspace Task section.
 */
import { transitionWords, type ActivityRow } from "./activity";
import type { ConversationTurnEntry } from "./conversation";
import { refSuffix, turnFailureWords } from "./conversation";
import { pendingAsks } from "./reduce";
import { TERMINAL_TASK_STATES, type AskView, type ConnectorWriteOutcome, type TaskSnapshot, type TaskSummary, type TransitionView, type WorkItemView } from "./types";

/**
 * The row ends at Check: a forward task rests in `validated` and never reaches a separate "Done" step (UX map D6), so the
 * last phase reads "Ready" once the check passed (`phasesFor`).
 */
export type Phase = "understand" | "plan" | "build" | "validate";
export const PHASES: readonly { key: Phase; label: string }[] = [
  { key: "understand", label: "Understand" },
  { key: "plan", label: "Plan" },
  { key: "build", label: "Build" },
  { key: "validate", label: "Check" },
];

export type PhaseState = "done" | "active" | "waiting" | "failed" | "stopped" | "todo";

export type StoryKind = "working" | "waiting" | "completed" | "failed" | "stopped" | "answered";

/** One line of a task's past: a state it reached, or a question the reader settled. */
export interface StoryRow {
  key: string;
  at: string | null;
  text: string;
  tone: "ok" | "warn" | "err" | "neutral";
}

export interface TurnPresentation {
  kind: StoryKind;
  /** One or two words: Working · Planning · Building · Validating · Applying · Waiting for you · Completed · Couldn't complete · Stopped. */
  statusLabel: string;
  /** The sentence under it — the newest activity, or the phase's own words. */
  statusDescription: string | null;
  /** Phase row, in order; `null` when the turn has no task (a plain answer needs no pipeline). */
  phases: { key: Phase; label: string; state: PhaseState }[] | null;
  /** The task this story is about, when there is one. */
  taskId: string | null;
  /** Build tally for the status line, "2 of 5", or null. */
  progress: { done: number; total: number; failed: number } | null;
  /** Activity rows for this turn/task, oldest first. */
  activity: ActivityRow[];
  /** Is work still happening server-side (turn live, or the task advancing without a turn)? */
  live: boolean;
  /**
   * When the current stretch of work started, for the elapsed clock: the send for a live turn
   * (set by the caller from `workingSince`), else the task's last transition — honest "since it
   * last moved" while the server advances the task between turns.
   */
  since: string | null;
  /** The machine code of a failed turn, shown muted as "(ref CODE)" beside the words; null when there is none. */
  refCode: string | null;
  /** The task rests in `validated` with nothing to decide: it is finished work, so the completion row (copy, export, feedback) shows. */
  atRest: boolean;
  /** "Cancel task" is meaningful: the task exists, is not over, is not applying and is not resting. */
  cancellable: boolean;
  /** Pause / Resume on offer (R3 `task_controls`), so the story strip can show the buttons. */
  controls: TaskControlsView;
  /** The pending ask the reader must answer, newest first, or null. */
  decision: AskView | null;
  /** How it ended, in words, when it ended badly or notably. */
  ending: { text: string; tone: "ok" | "warn" | "bad" } | null;
  /** Something failed: the words, and whether "try again" (resend the request) makes sense. */
  failure: { text: string; retryable: boolean } | null;
  /**
   * The task's path, oldest first: R3 `transitions[]` in words with this task's settled asks folded
   * in ("You approved the plan"), so a reopened session still has a story and no answered question
   * floats loose in the transcript. Empty without a task.
   */
  history: StoryRow[];
  /** What concretely went wrong when it ended badly: components that failed and why, validation
   *  failures, the pause reason. Empty unless the task failed. */
  details: string[];
}

export interface StoryContext {
  snapshots: Record<string, TaskSnapshot>;
  tasks: readonly TaskSummary[];
  asks: readonly AskView[];
  activity: readonly ActivityRow[];
  activeTurnId: string | null;
}

const PHASE_OF_STATE: Record<string, Phase> = {
  exploring: "understand",
  plan_proposed: "plan",
  contracting: "build",
  building: "build",
  amendment_proposed: "build",
  needs_input: "build",
  needs_budget: "build",
  assembling: "build",
  changeset_proposed: "validate",
  checking: "validate",
  validated: "validate",
  applying: "validate",
  applied: "validate",
};

/** States where the task is waiting on the reader rather than working. */
const WAITING_STATES: ReadonlySet<string> = new Set([
  "plan_proposed",
  "amendment_proposed",
  "needs_input",
  "needs_budget",
  "changeset_proposed",
  "validated",
  "held",
]);

const STATE_WORDS: Record<string, string> = {
  exploring: "Understanding your request",
  plan_proposed: "Plan ready for your review",
  contracting: "Preparing the build",
  building: "Building the change",
  amendment_proposed: "A change to the plan needs your decision",
  needs_input: "Needs something from you",
  needs_budget: "Needs more budget from you",
  assembling: "Preparing the changes",
  changeset_proposed: "Changes ready for your review",
  checking: "Checking against the org",
  validated: "Built and checked; ready to save or check again",
  held: "Paused",
  applying: "Applying to the org",
  applied: "Applied to the org",
  cancelled: "The task was cancelled",
  failed: "The task could not be completed",
};

/** The resting verdict's plain words per `rest_verdict.code` (contracts `RestVerdictView`); `passed` is the only green one. */
const REST_VERDICT_WORDS: Readonly<Record<string, string>> = {
  coverage_shortfall: "Built; the org check ran but code coverage fell short. You can still save, check again, or ask for changes",
  recheck_failed: "Built; the latest re-check failed. You can still save, check again, or ask for changes",
  nothing_deployed: "Built; nothing in this change deploys, so there was nothing to check",
};
/** Why a check did not run: MSA `PolicyRef` values a `not_run` verdict carries (`work/settle.py`), worded like `UNCHECKED_DETAIL`; never rendered verbatim. */
const NOT_RUN_REASONS: Readonly<Record<string, string>> = {
  package_too_large: "the deploy zip is over the size limit",
  check_unavailable: "the check could not run to a result",
  org_check_cancelled: "the org cancelled the check",
  org_submit_refused: "the org refused the submission",
};
const NOT_RUN_FALLBACK = "a policy held it";
const REST_VERDICT_UNKNOWN = "Built; check result not understood";

export interface RestingVerdictWords {
  /** Only `passed` is ready; every other code (known or not) is not. */
  passed: boolean;
  /** A check that ran and failed (`recheck_failed`, `coverage_shortfall`, or legacy `last_check.ok === false`): the only case that may say "failed". */
  failed: boolean;
  /** ok (passed only), warn (failed or did not run), neutral (nothing to check, unknown). */
  tone: "ok" | "warn" | "neutral";
  /** The short state word for badges. */
  label: string;
  words: string;
}

/**
 * The ONE reading of a resting task's check verdict. A non-null `rest_verdict` is the only source (`last_check.ok` is not
 * consulted); `last_check.ok` is the fallback only for a null/absent field (older server). Null when the task does not rest
 * in `validated`, or no verdict can be read.
 */
export function restingVerdictOf(snapshot: Pick<TaskSnapshot, "state" | "validation"> & Partial<Pick<TaskSnapshot, "rest_verdict">> | null | undefined): RestingVerdictWords | null {
  if (!snapshot || snapshot.state !== "validated") return null;
  const v = snapshot.rest_verdict;
  if (v) {
    if (v.code === "passed") return { passed: true, failed: false, tone: "ok", label: STATE_LABEL.validated, words: STATE_WORDS.validated };
    if (v.code === "not_run") {
      const reason = (v.policy && NOT_RUN_REASONS[v.policy]) || NOT_RUN_FALLBACK;
      return { passed: false, failed: false, tone: "warn", label: "Needs attention", words: `Built; the org check did not run because ${reason}. You can still save or check again` };
    }
    if (v.code === "nothing_deployed") return { passed: false, failed: false, tone: "neutral", label: "Nothing to check", words: REST_VERDICT_WORDS.nothing_deployed! };
    const words = REST_VERDICT_WORDS[v.code];
    if (words) return { passed: false, failed: true, tone: "warn", label: "Needs attention", words };
    return { passed: false, failed: false, tone: "neutral", label: "Built", words: REST_VERDICT_UNKNOWN };
  }
  if (snapshot.validation?.last_check?.ok === false) return { passed: false, failed: true, tone: "warn", label: "Needs attention", words: RESTING_FAILED_WORDS };
  return null;
}

/** A task resting in `validated` whose check ran and failed (rule 23: never read as a pass). Not true for a check that did not run. */
export function restingCheckFailed(snapshot: Pick<TaskSnapshot, "state" | "validation"> & Partial<Pick<TaskSnapshot, "rest_verdict">> | null | undefined): boolean {
  return !!restingVerdictOf(snapshot)?.failed;
}

/** A held task's headline, from R3 `hold_reason` (MSA `HoldReason`): a budget hold and a person's hold read differently. */
export function heldLabel(reason: string | null | undefined): string {
  if (reason === "budget") return "Paused: usage budget used up";
  if (reason === "user") return "Paused at your request";
  return "Paused";
}

/** What to do about a hold, in words: the Resume button when R3 offers it, else the chat "continue" (which works too). */
export function heldWords(reason: string | null | undefined, canResume = false): string {
  if (canResume) return reason === "budget" ? "Resume once your usage budget allows." : "Resume to pick it up again.";
  return reason === "budget" ? "Say \u201ccontinue\u201d once your usage budget allows." : "Say \u201ccontinue\u201d to pick it up again.";
}

/** The route-only controls R3 offers (`hold` / `resume`), and whether a requested hold is still waiting for the component in flight. */
export interface TaskControlsView {
  hold: boolean;
  resume: boolean;
  holdPending: boolean;
  /** The task is in `building`: only there is the pause "after this component" (assembling / checking pause at their own boundary). */
  building: boolean;
}

export const NO_CONTROLS: TaskControlsView = { hold: false, resume: false, holdPending: false, building: false };

/** Controls from R3 `task_controls` / `hold_pending`; a finished task offers none whatever the list says. */
export function taskControlsOf(snapshot: Pick<TaskSnapshot, "state" | "task_controls" | "hold_pending"> | null | undefined): TaskControlsView {
  if (!snapshot || TERMINAL_TASK_STATES.has(snapshot.state)) return NO_CONTROLS;
  const offered = snapshot.task_controls ?? [];
  const holdPending = !!snapshot.hold_pending;
  return { hold: offered.includes("hold") && !holdPending, resume: offered.includes("resume"), holdPending, building: snapshot.state === "building" };
}

export const HOLD_PENDING_WORDS = "Pausing after this component\u2026";
export const HOLD_PENDING_WORDS_NEUTRAL = "Pausing\u2026";

const RESTING_FAILED_WORDS = "The last check failed; you can still save, check again, or ask for changes";

const STATE_LABEL: Record<string, string> = {
  exploring: "Working",
  plan_proposed: "Waiting for you",
  contracting: "Building",
  building: "Building",
  amendment_proposed: "Waiting for you",
  needs_input: "Waiting for you",
  needs_budget: "Waiting for you",
  assembling: "Building",
  changeset_proposed: "Waiting for you",
  checking: "Checking",
  validated: "Ready",
  held: "Paused",
  applying: "Applying",
  applied: "Completed",
  cancelled: "Stopped",
  superseded: "Replaced",
  failed: "Couldn't complete",
};

/** History list rows: what a reader would say when choosing what to reopen (the longer phrases; same owner as the story labels). */
const HISTORY_STATE_WORDS: Readonly<Record<string, string>> = {
  exploring: "Looking into it",
  plan_proposed: "Plan ready to review",
  contracting: "Planning",
  building: "Building",
  amendment_proposed: "Plan change to review",
  needs_input: "Waiting for your answer",
  needs_budget: "Paused: usage budget used up",
  assembling: "Putting the changes together",
  changeset_proposed: "Changes ready to review",
  checking: "Checking the changes",
  validated: "Ready to save",
  applying: "Saving",
  applied: "Saved",
  cancelled: "Cancelled",
  failed: "Needs attention",
  held: "Paused",
  superseded: "Replaced by a newer change",
};

/**
 * The ONE owner of a task state's short plain word, in two contexts: the turn story's badge (`STATE_LABEL`) and the
 * History list's longer phrases (`HISTORY_STATE_WORDS`). An unlisted (future) state falls back to its own name with the
 * underscores removed, so a new state is never blank.
 */
export function taskStateLabel(state: string, context: "story" | "history" = "story"): string {
  const table = context === "history" ? HISTORY_STATE_WORDS : STATE_LABEL;
  return table[state] ?? state.replace(/_/g, " ");
}

export function buildProgress(snapshot: TaskSnapshot | null): TurnPresentation["progress"] {
  const build = snapshot?.build;
  if (!build || !build.items.length) return null;
  const counts: Record<string, number> = {};
  for (const item of build.items) counts[item.status] = (counts[item.status] ?? 0) + 1;
  return {
    done: (counts.built ?? 0) + (counts.validated ?? 0),
    total: build.items.length,
    failed: (counts.failed ?? 0) + (counts.blocked ?? 0),
  };
}

/**
 * The phase row for a task state. A task that failed or was stopped is marked where it BROKE
 * (`brokeFrom`: the state the last transition left), not on Done: "Build ✗, Validate –" tells the
 * reader how far it got; "Done ✗" only says it did not finish.
 */
export function phasesFor(state: string, brokeFrom: string | null = null, notReady = false): TurnPresentation["phases"] {
  const over = state === "failed" || state === "cancelled";
  // A paused task is marked where it paused (the state the hold left); an unknown origin reads as the build.
  const current: Phase | undefined = over
    ? (brokeFrom && PHASE_OF_STATE[brokeFrom]) || "validate"
    : state === "held"
      ? (brokeFrom && PHASE_OF_STATE[brokeFrom]) || "build"
      : PHASE_OF_STATE[state];
  if (!current) return null;
  const idx = PHASES.findIndex((p) => p.key === current);
  return PHASES.map((p, i) => {
    let s: PhaseState = "todo";
    let label = p.label;
    if (i < idx) s = "done";
    else if (i === idx) {
      if (state === "applied") s = "done";
      else if (state === "validated" && !notReady) {
        // The end of the row: checked and ready (the old "Done" step is unreachable for a forward task).
        s = "done";
        label = "Ready";
      } else if (over) {
        // A cancelled task (a declined plan, a replaced or closed change) STOPPED there; only a failed one failed.
        // Same split as the task panel's tracker (`trackerModel`: cancelled -> "stopped"); dev 2026-11-02 "Plan: failed" on a decline.
        s = state === "cancelled" ? "stopped" : "failed";
      }
      else if (WAITING_STATES.has(state)) s = "waiting";
      else s = "active";
    }
    return { key: p.key, label, state: s };
  });
}

// ── how it ended, and why ────────────────────────────────────────────────────────────────────────

/** MSA `TransitionReason` (`core/enums.py`): the machine's own typed reason for a terminal state. */
const ENDING_BY_REASON: Record<string, string> = {
  ask_expired_exhausted: "A question went unanswered for too long, so the task was closed.",
  revert_org_drifted: "The org changed while the revert was waiting, so it was closed. Ask for a fresh revert if you still want it.",
};

/** MSA `TransitionReason.SUPERSEDED_BY_SESSION_CLOSE`: the person closed the session while the task rested at validated (D1). */
export const SUPERSEDED_BY_SESSION_CLOSE = "superseded_by_session_close";
/** The server's words for that ending (`core/reasons.py`); the change stays savable from History. */
export const SESSION_CLOSED_WORDS = "Closed. Your validated change stays savable from History.";

/** The phase a state belongs to, as the words "while …" needs. */
const PHASE_DOING: Record<Phase, string> = {
  understand: "understanding your request",
  plan: "working on the plan",
  build: "building the change",
  validate: "checking the changes",
};

function lastTransition(snapshot: TaskSnapshot): TransitionView | null {
  const ts = snapshot.transitions ?? [];
  return ts.length ? ts[ts.length - 1] : null;
}

/**
 * Why a task ended the way it did, from the last R3 transition: its `reason` when the machine gave
 * one, else its `event` and the state it left. The machine ends a task in exactly these ways
 * (`core/machine.py`): `apply_finished` not ok and `unrecoverable` → failed; `rejected`,
 * `cancel_requested`, an exhausted `ask_expired` and a drifted revert → cancelled.
 */
export function endingWords(snapshot: TaskSnapshot): string {
  // A session close supersedes a resting validated task with its own typed reason, checked before the cause (a newer request has none).
  // The words promise a Save, so they are only said while the snapshot still offers one; otherwise just "Closed."
  if (lastTransition(snapshot)?.reason === SUPERSEDED_BY_SESSION_CLOSE) {
    return snapshot.actions?.includes("save") ? SESSION_CLOSED_WORDS : "Closed.";
  }
  // `ended.cause` is the machine's own typed word for why (core/task_ended.py): a validated task closed by a newer request.
  if (snapshot.ended?.cause === "superseded") {
    return snapshot.actions?.includes("save") ? "Replaced by your newer request. This change is still savable." : "Replaced by your newer request.";
  }
  const last = lastTransition(snapshot);
  if (last?.reason && ENDING_BY_REASON[last.reason]) return ENDING_BY_REASON[last.reason];
  const from = last?.from ?? null;
  switch (last?.event) {
    case "cancel_requested":
      return "You stopped the task.";
    case "rejected":
      return from === "plan_proposed" ? "You declined the plan, so nothing was changed." : "You declined the changes, so nothing was applied.";
    case "apply_finished":
      return "Applying the changes to the org failed. Nothing is left half-applied: the org rejects a deployment as a whole.";
    case "unrecoverable": {
      const phase = from ? PHASE_OF_STATE[from] : undefined;
      if (from === "applying") return "Something went wrong while applying the changes, and the task couldn't continue.";
      return phase ? `Something went wrong while ${PHASE_DOING[phase]}, and the task couldn't continue.` : "Something went wrong and the task couldn't continue.";
    }
    default:
      return snapshot.state === "cancelled" ? "The task was cancelled." : "The task could not be completed.";
  }
}

/** Work-item `error_code`s (MSA `work/api.py` `ITEM_ERROR_*`, the artifact and repair codes) in
 *  a reader's words; the sentences follow `core/reasons.py`, which the server does not put on the
 *  wire yet. An unknown code gets plain words plus a "(ref CODE)" suffix, never dropped. */
const ITEM_ERROR_WORDS: Record<string, string> = {
  unsupported_type: "this kind of component can't be built here yet",
  base_unavailable: "its current version couldn't be read from the org",
  derived_nothing_to_follow: "a follow-on change couldn't be worked out",
  derived_targets_unbuilt: "the components it depends on weren't built",
  op_mismatch: "the org no longer matches the plan for it",
  lease_lapsed: "the build stopped partway and used up its attempts",
  subject_not_validated: "the component it tests wasn't validated",
  contract_unavailable: "an interface contract it needs was missing",
  artifact_invalid: "what was built couldn't be stored as a valid file",
  artifact_too_large: "what was built is over the size limit",
  artifact_namespace_invalid: "it uses a namespace prefix that can't be resolved",
  artifact_no_files: "the build returned no files for it",
  repair_exhausted: "it still failed its checks after every repair round",
  loop_exhausted: "the build ran out of steps before finishing it",
  output_truncated: "the generated output was cut off at the length limit",
  invalid_output: "the generated output was malformed",
  contract_conformance_failed: "what was built doesn't match its interface contract",
  unsupported_field_type: "it uses a field type that can't be generated here",
  test_asserts_removed: "a repair weakened the test, so it was refused",
};

function itemFailureWords(item: WorkItemView, byId: ReadonlyMap<string, WorkItemView>): string {
  if (item.status === "blocked") {
    const names = (item.depends_on ?? []).map((id) => byId.get(id)?.full_name ?? null).filter((n): n is string => !!n);
    return names.length ? `waiting on ${names.join(", ")}` : "blocked by an earlier failure";
  }
  const code = item.error_code;
  if (!code) return "couldn't be built";
  // `deviation:<kind>` / `unavailable:<reason>` are the driver's prefixed codes; the prefix is the words.
  if (code.startsWith("deviation:")) return "needs something the plan didn't cover";
  if (code.startsWith("unavailable:")) return "the build service couldn't produce it";
  // An unknown code gets plain words and a muted reference, never the lowercased code as the sentence.
  return ITEM_ERROR_WORDS[code] ?? `couldn't be built ${refSuffix(code)}`;
}

const DETAIL_LIMIT = 4;

/** The concrete facts behind a failure, from R3 alone: never a guess at a cause the server did not state. */
export function failureDetails(snapshot: TaskSnapshot): string[] {
  const out: string[] = [];
  const items = snapshot.build?.items ?? [];
  // `depends_on` names work items (as `BuildProgressView` resolves them); plan item ids are accepted too.
  const byId = new Map<string, WorkItemView>();
  for (const i of items) {
    byId.set(i.work_item_id, i);
    if (i.item_id) byId.set(i.item_id, i);
  }
  const failed = items.filter((i) => i.status === "failed" || i.status === "blocked");
  for (const item of failed.slice(0, DETAIL_LIMIT)) out.push(`${item.full_name}: ${itemFailureWords(item, byId)}`);
  if (failed.length > DETAIL_LIMIT) out.push(`and ${failed.length - DETAIL_LIMIT} more components`);
  const check = snapshot.validation?.last_check;
  if (check && check.ok === false) {
    const failures = Object.entries(check.failures ?? {});
    if (failures.length) {
      for (const [component, messages] of failures.slice(0, DETAIL_LIMIT)) {
        const text = Array.isArray(messages) ? messages.join("; ") : messages;
        out.push(text ? `${component}: ${text}` : `${component} failed the org's check`);
      }
      if (failures.length > DETAIL_LIMIT) out.push(`and ${failures.length - DETAIL_LIMIT} more validation failures`);
    } else if (check.detail) {
      out.push(check.detail);
    }
  }
  return out;
}

// ── the task's history ───────────────────────────────────────────────────────────────────────────

const HISTORY_TONE: Record<string, StoryRow["tone"]> = { applied: "ok", validated: "ok", cancelled: "warn", failed: "err" };

const ASK_SUBJECT_WORDS: Record<string, string> = {
  approve_plan: "the plan",
  approve_plan_revision: "the plan",
  approve_amendment: "the change to the plan",
  approve_changeset: "the changes",
  approve_apply: "applying the changes",
  confirm_revert: "the revert",
  approve_overwrite: "overwriting the org's version",
  approve_delete: "the deletion",
  // MSA leg `cv-connector-writes`: a write to a connected external tool (Notion, Jira, …)
  approve_connector_write: "the write to the connected tool",
};

/**
 * MSA leg `cv-connector-writes`: the settled line of an approved write once its outcome is known — the provider accepted it
 * (the title it reports, the link is rendered beside the line) or refused it (the provider's own one-line reason, verbatim).
 */
export function connectorWriteWords(outcome: ConnectorWriteOutcome): string {
  const provider = outcome.provider || "the connected tool";
  if (outcome.ok && outcome.action === "attach") {
    return `Attached ${outcome.title ? `“${outcome.title}”` : "the file"} in ${provider}`;
  }
  if (outcome.ok) return `Saved to ${provider}${outcome.title ? `: “${outcome.title}”` : ""}`;
  const who = provider.charAt(0).toUpperCase() + provider.slice(1);
  return `${who} did not accept the change${outcome.error ? `: ${outcome.error.replace(/\.$/, "")}` : ""}. Nothing was changed.`;
}

/**
 * A settled ask as one line of the story: what the reader did, or what became of the question. `outcome` (an approved
 * connector write's result, when the client has it) turns "You approved the write" into what the write did.
 */
export function askRecordWords(ask: AskView, outcome: ConnectorWriteOutcome | null = null): StoryRow["text"] {
  const kind = ask.interaction.kind;
  const r = ask.resolution?.resolution;
  const optionId = r?.option_id ?? null;
  const subject = ASK_SUBJECT_WORDS[kind] ?? null;
  if (ask.status === "answered") {
    if (kind === "approve_connector_write" && outcome && (optionId === "approve" || r?.approved === true)) {
      return connectorWriteWords(outcome);
    }
    if (subject && (optionId === "approve" || r?.approved === true)) return `You approved ${subject}`;
    if (subject && (optionId === "decline" || r?.approved === false)) return `You declined ${subject}`;
    if (optionId === "ack") return "You acknowledged the pause";
    const label = optionId ? ask.interaction.options.find((o) => o.option_id === optionId)?.label ?? null : null;
    if (label) return `You chose: ${label}`;
    if (r?.free_text) return `You replied: ${r.free_text.length > 80 ? `${r.free_text.slice(0, 79)}…` : r.free_text}`;
    return "You answered";
  }
  if (ask.status === "superseded" || ask.status === "withdrawn") return "A question was replaced by a newer one";
  if (ask.status === "expired") return "A question expired without an answer";
  return `A question was closed (${ask.status})`;
}

export function askRecordTone(ask: AskView, outcome: ConnectorWriteOutcome | null = null): StoryRow["tone"] {
  if (ask.status === "answered") {
    if (outcome && !outcome.ok) return "warn";
    return ask.resolution?.resolution?.option_id === "decline" ? "warn" : "ok";
  }
  return ask.status === "expired" ? "warn" : "neutral";
}

/** Transitions in words (a state reached twice in a row is said once) with the task's settled asks, by time. */
export function historyFor(snapshot: TaskSnapshot | null, asks: readonly AskView[]): StoryRow[] {
  if (!snapshot) return [];
  const rows: StoryRow[] = [];
  const failedAtRest = restingCheckFailed(snapshot);
  const transitions = snapshot.transitions ?? [];
  for (const [i, t] of transitions.entries()) {
    const text = transitionWords(t.to);
    const prev = rows[rows.length - 1];
    if (prev && prev.text === text) continue;
    rows.push({ key: `t:${t.epoch}:${i}`, at: t.at ?? null, text, tone: failedAtRest && t.to === "validated" && i === transitions.length - 1 ? "warn" : HISTORY_TONE[t.to] ?? "neutral" });
  }
  for (const ask of asks) {
    if (ask.task_id !== snapshot.task_id || ask.status === "pending") continue;
    rows.push({
      key: `ask:${ask.interaction.interaction_id}`,
      at: ask.resolution?.resolved_at ?? ask.created_at ?? null,
      text: askRecordWords(ask),
      tone: askRecordTone(ask),
    });
  }
  return rows.sort((a, b) => (a.at ?? "").localeCompare(b.at ?? ""));
}

/**
 * A built change resting for the person's save / validate: nothing advances it, so a NEW turn is not about it until the server says so
 * (the turn's own `task_id`). Dev 2026-11-02: a plain question borrowed it and showed "Waiting for you · Changes ready" while answering.
 */
const RESTING_STATES: ReadonlySet<string> = new Set(["changeset_proposed", "validated"]);

/** The task a turn is about: its own `task_id`, else (for the live turn) the newest open task that is still moving. */
export function taskForTurn(entry: ConversationTurnEntry, ctx: StoryContext): TaskSnapshot | null {
  if (entry.taskId && ctx.snapshots[entry.taskId]) return ctx.snapshots[entry.taskId];
  if (!entry.live) return null;
  const open = [...ctx.tasks]
    .filter((t) => !TERMINAL_TASK_STATES.has(t.state) && !RESTING_STATES.has(t.state) && ctx.snapshots[t.task_id])
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
  return open ? ctx.snapshots[open.task_id] : null;
}

function rowsFor(entry: ConversationTurnEntry, taskId: string | null, ctx: StoryContext): ActivityRow[] {
  return ctx.activity.filter(
    (r) => (entry.turnId && r.turnId === entry.turnId) || (taskId && r.taskId === taskId),
  );
}

function tally(progress: TurnPresentation["progress"]): string | null {
  if (!progress) return null;
  const parts = [`${progress.done}/${progress.total} components built`];
  if (progress.failed) parts.push(`${progress.failed} failed`);
  return parts.join(" · ");
}

/**
 * The story of one turn. `decision` is the newest pending ask bound to the turn's task, or a
 * task-less ask raised by this turn.
 */
export function presentTurn(entry: ConversationTurnEntry, ctx: StoryContext): TurnPresentation {
  const snapshot = taskForTurn(entry, ctx);
  const taskId = snapshot?.task_id ?? null;
  const activity = rowsFor(entry, taskId, ctx);
  const newest = activity.length ? activity[activity.length - 1] : null;
  // Only something still in progress narrates the status line; a finished row (a failure, a built
  // item) stays in the rows below while the line says what the task is doing now.
  const current = newest && newest.done === false ? newest.text : null;
  const pending = pendingAsks(ctx.asks).filter((a) => (taskId ? a.task_id === taskId : a.task_id === null));
  const decision = pending.length ? pending[pending.length - 1] : null;
  const progress = buildProgress(snapshot);

  // A local send that failed, or a server turn that ended badly.
  if (entry.failure) {
    return {
      kind: "failed",
      statusLabel: "Couldn't send",
      statusDescription: entry.failure,
      phases: null,
      taskId,
      progress: null,
      activity,
      live: false,
      since: null,
      refCode: null,
      atRest: false,
      cancellable: false,
      controls: NO_CONTROLS,
      decision: null,
      ending: { text: entry.failure, tone: "bad" },
      failure: { text: entry.failure, retryable: true },
      history: [],
      details: [],
    };
  }

  if (!snapshot) {
    if (entry.live) {
      return {
        kind: "working",
        statusLabel: "Working",
        statusDescription: current ?? "Understanding your request",
        phases: null,
        taskId: null,
        progress: null,
        activity,
        live: true,
        since: null,
        refCode: null,
        atRest: false,
        cancellable: false,
      controls: NO_CONTROLS,
        decision,
        ending: null,
        failure: null,
        history: [],
        details: [],
      };
    }
    const failed = entry.outcome === "failed";
    const stopped = entry.outcome === "cancelled";
    // The server's own words own how it ended (`TurnOut.markdown`, the assistant message): with text
    // present the strip keeps only the label and, for a failure, the machine code; without it (an
    // older server, or a reload — R5 carries none) the client's full sentence stays.
    const serverText = entry.turn.assistant.length > 0;
    const failedText = serverText ? null : turnFailureWords(entry.errorCode);
    const refCode = failed ? entry.errorCode : null;
    const stoppedText = serverText ? null : "Stopped before finishing.";
    // A task-less decision (a clarify) is told by its card alone; the turn has no story strip.
    return {
      kind: failed ? "failed" : stopped ? "stopped" : "answered",
      statusLabel: failed ? "Couldn't complete" : stopped ? "Stopped" : "Answered",
      statusDescription: failed ? failedText : stopped ? stoppedText : null,
      phases: null,
      taskId: null,
      progress: null,
      activity,
      live: false,
      since: null,
      refCode,
      atRest: false,
      cancellable: false,
      controls: NO_CONTROLS,
      decision,
      ending: failed && failedText ? { text: failedText, tone: "bad" } : stopped && stoppedText ? { text: stoppedText, tone: "warn" } : null,
      failure: failed ? { text: failedText ?? turnFailureWords(null), retryable: true } : null,
      history: [],
      details: [],
    };
  }

  const state = snapshot.state;
  const terminal = TERMINAL_TASK_STATES.has(state);
  // The person's own message is being worked on (e.g. an answer revising the plan): that is Working, never "Waiting for you" —
  // dev 2026-11-02 showed "Waiting for you · Plan ready for your review" over "Reading source code" for 1m 22s.
  const answering = entry.live && !terminal;
  const waiting = !answering && (WAITING_STATES.has(state) || !!decision);
  const live = !terminal && !waiting;
  const atRest = state === "validated" && !decision;
  const verdict = restingVerdictOf(snapshot);
  // Anything but a `passed` verdict at rest is not Ready (a failed check, one that did not run, nothing to check, unknown).
  const notPassed = !!verdict && !verdict.passed;
  const held = state === "held";
  const superseded = state === "cancelled" && snapshot.ended?.cause === "superseded";
  const controls = taskControlsOf(snapshot);
  const words = notPassed ? verdict!.words : held ? heldWords(snapshot.hold_reason, controls.resume) : STATE_WORDS[state] ?? state.replace(/_/g, " ");
  // D6: a passing check at rest is "Ready" (done); a failed one is "Needs attention" (amber, still at rest).
  const label = answering
    ? "Working"
    : decision && !terminal
    ? "Waiting for you"
    : held
      ? heldLabel(snapshot.hold_reason)
      : atRest && notPassed
        ? verdict!.label
        : superseded
          ? "Replaced"
          : STATE_LABEL[state] ?? "Working";
  const kind: StoryKind =
    state === "failed"
      ? "failed"
      : state === "cancelled"
        ? "stopped"
        : state === "applied" || (atRest && !notPassed)
          ? "completed"
          : waiting
            ? "waiting"
            : "working";
  const description =
    answering ? current ?? "Working on your message"
    : kind === "working" ? (controls.holdPending ? (controls.building ? HOLD_PENDING_WORDS : HOLD_PENDING_WORDS_NEUTRAL) : current ?? words) : kind === "waiting" ? words : state === "applied" ? tally(progress) ?? words : words;
  const last = lastTransition(snapshot);
  const lastMoved = last?.at ?? snapshot.updated_at ?? null;
  const t = tally(progress);
  const inBuild = PHASE_OF_STATE[state] === "build";
  const ending: TurnPresentation["ending"] =
    state === "failed"
      ? { text: endingWords(snapshot), tone: "bad" }
      : state === "cancelled"
        ? { text: endingWords(snapshot), tone: "warn" }
        : state === "applied"
          ? { text: t ? `Applied ${progress!.total} ${progress!.total === 1 ? "component" : "components"} to the org.` : words, tone: "ok" }
          : null;
  return {
    kind,
    statusLabel: kind === "working" && t && inBuild ? `${label} · ${t}` : label,
    // Once over, the ending is the sentence; while waiting in the build, the tally rides along.
    statusDescription: ending ? ending.text : kind === "waiting" && t && inBuild ? `${description} · ${t}` : description,
    // A replaced change was finished work: its row still ends at Ready, not at a failed Check.
    phases: superseded ? phasesFor("validated", null, false) : phasesFor(state, last?.from ?? null, notPassed),
    taskId,
    progress,
    activity,
    live: live || entry.live,
    since: live ? lastMoved : null,
    refCode: null,
    atRest,
    cancellable: !terminal && state !== "applying" && !atRest,
    controls,
    decision,
    ending,
    failure: state === "failed" ? { text: ending!.text, retryable: false } : null,
    history: historyFor(snapshot, ctx.asks),
    details: state === "failed" ? failureDetails(snapshot) : [],
  };
}
